import { useState, useMemo } from "react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import Box from "@mui/material/Box";
import { useRef } from "react";
import Avatar from "../../assets/img/avatar.png";
import CustomToolbar from "./CustomToolbar";
import { PlayCircleOutline } from "@mui/icons-material";
import CustomButton from "../UI/CustomButton";
import { TYPES_OF_REPORTS } from "../../utils/constants";

const CHARACTER_LIMITS = {
  "SiteID-ParticipantID": 10,
  name: 54,
  visit_name: 54,
  visit_status: 12,
  photos_taken: 1,
  photo_type: 54,
  video_type: 54,
  image_name: 150,
  video_name: 150,
  created_at: 9,
};

const truncateText = (text, limit) => {
  if (text && text.length > limit) {
    return `${text.substring(0, limit)}...`;
  }
  return text;
};

const handleRowClick = (trialId) => (params) => {
  const trialQuery = trialId ? `?trial=${trialId}` : "";
  window.open(`/patient/${params.row?.subject_id}/info${trialQuery}`).focus();
};

const EMPTY_SELECTION = {
  ids: new Set(),
  type: "include",
};

const ReportColumns = ({
  reportData,
  trialDetails,
  reportName,
  role,
  reportDownloadLink,
  downloadReport,
  paginationModel,
  setPaginationModel,
  totalCount,
  data,
  reportTableConfigurations,
  setFilterConfigurationLastStateSaved,
  isZipLoading,
}) => {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState(EMPTY_SELECTION);

  const columnData = useMemo(() => {
    let base = [["name", "STUDYID", 300]];

    switch (trialDetails?.selected_report) {
      case TYPES_OF_REPORTS.VISIT:
        base = [
          ["name", "STUDYID", 300],
          ["site_id", "SITENUM", 300],
          ["participant_number", "SCREENNUM", 300],
          ["formatted_intials", "SUBJID", 300],
          ["visit_name", "VISITNUM", 300],
          ["visit_start_date", "VISITDATE", 300],
          ["clinical_trial_status", "VISITSTATUS", 300],
        ];
        break;
      case TYPES_OF_REPORTS.ECRF:
        base = [
          ["name", "STUDYID", 300],
          ["site_id", "SITENUM", 300],
          ["participant_number", "SCREENNUM", 300],
          ["visit_name", "VISITNUM", 300],
          ["visit_status", "VISITSTATUS", 300],
          ["block_name", "FORMNAME", 300],
          ["key_value", "Value", 900],
        ];
        break;
      case TYPES_OF_REPORTS.PHOTO:
        base = [
          ["name", "STUDYID", 300],
          ["site_id", "SITENUM", 300],
          ["participant_number", "SCREENNUM", 300],
          ["SiteID-ParticipantID", "SUBJID", 300],
          ["visit_name", "VISITNUM", 300],
          ["visit_status", "VISITSTATUS", 300],
          ["image_name", "Photo File Name", 300],
          ["created_at", "Captured At", 300],
          ["photos_taken", "Photos Taken", 150],
          ["photo_type", "Photo Type", 300],
        ];
        break;
      case TYPES_OF_REPORTS.VIDEO:
        base = [
          ["name", "STUDYID", 300],
          ["site_id", "SITENUM", 300],
          ["participant_id", "SCREENNUM", 300],
          ["SiteID-ParticipantID", "SUBJID", 300],
          ["visit_name", "VISITNUM", 300],
          ["visit_status", "VISITSTATUS", 300],
          ["video_type", "Video Type", 300],
          ["video_name", "Video File Name", 300],
          ["created_at", "Captured At", 300],
        ];
        break;
      case TYPES_OF_REPORTS.EXTERNAL_PHOTOS:
        base = [
          ["name", "STUDYID", 300],
          ["site_id", "SITENUM", 300],
          ["participant_number", "SCREENNUM", 300],
          ["SiteID-ParticipantID", "SUBJID", 300],
          ["visit_name", "VISITNUM", 300],
          ["visit_status", "VISITSTATUS", 300],
          ["image_name", "Photo File Name", 300],
          ["created_at", "Uploaded At", 300],
        ];
        break;
      case TYPES_OF_REPORTS.EXTERNAL_VIDEOS:
        base = [
          ["name", "STUDYID", 300],
          ["site_id", "SITENUM", 300],
          ["participant_number", "SCREENNUM", 300],
          ["SiteID-ParticipantID", "SUBJID", 300],
          ["visit_name", "VISITNUM", 300],
          ["visit_status", "VISITSTATUS", 300],
          ["video_type", "Video Type", 300],
          ["video_name", "Video File Name", 300],
          ["created_at", "Uploaded At", 300],
        ];
        break;
      case TYPES_OF_REPORTS.EXTERNAL_DOCUMENTS:
        base = [
          ["name", "STUDYID", 300],
          ["site_id", "SITENUM", 300],
          ["participant_number", "SCREENNUM", 300],
          ["SiteID-ParticipantID", "SUBJID", 300],
          ["visit_name", "VISITNUM", 300],
          ["visit_status", "VISITSTATUS", 300],
          ["document_name", "Document File Name", 300],
          ["created_at", "Uploaded At", 300],
        ];
        break;
      case TYPES_OF_REPORTS.POEM: {
        base = [
          ["name", "STUDYID", 300],
          ["site_id", "SITENUM", 300],
          ["participant_number", "SCREENNUM", 300],
          ["SiteID-ParticipantID", "SUBJID", 300],
          ["visit_name", "VISITNUM", 300],
          ["visit_open_date", "VISITDATE", 300],
          ["visit_status", "VISITSTATUS", 300],
        ];
        if (!["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role)) {
          base.push(
            ["first_name", "Subject First Name", 300],
            ["last_name", "Subject Last Name", 300]
          );
        }
        break;
      }
      case TYPES_OF_REPORTS.ADVANCED_SURVEY:
      case TYPES_OF_REPORTS.EDIARY: {
        base = [
          ["name", "STUDYID", 300],
          ["site_id", "SITENUM", 300],
          ["participant_number", "SCREENNUM", 300],
          ["visit_name", "VISITNUM", 300],
          ["visit_status", "VISITSTATUS", 300],
        ];
        // if (!["CRO Admin", "Cro_coordinator", "Data Manager"].includes(role)) {
        //   base.push(
        //     ["first_name", "Subject First Name", 300],
        //     ["last_name", "Subject Last Name", 300]
        //   );
        // }
        break;
      }
      case TYPES_OF_REPORTS.ECRF_DISCREPANCY:
        base = [
          ["site_participant_id", "SUBJID", 160],
          ["visit_name", "VISITNUM", 130],
          ["site_id", "SITENUM", 120],
          ["visit_status", "VISITSTATUS", 150],
          ["inclinic_start_date", "In-Clinic Start Date", 180],
          ["inclinic_completed_date", "In-Clinic Completed Date", 200],
          ["ecrf_name", "eCRF Name", 220],
          ["discrepancy_type", "Discrepancy Type", 220],
        ];
        break;
      case TYPES_OF_REPORTS.AI_SCORE:
        base = [
          ["name", "STUDYID", 300],
          ["site_id", "SITENUM", 150],
          ["participant_number", "SCREENNUM", 150],
          ["SiteID-ParticipantID", "SUBJID", 150],
          ["visit_name", "VISITNUM", 300],
          ["visit_status", "VISITSTATUS", 150],
          ["area", "Area", 200],
          ["photo_type", "Photo Type", 300],
          ["face_type", "Face Type", 120],
          ["score", "AI Score", 100],
          ["evaluated_at", "Evaluated At", 180],
        ];
        break;
      case TYPES_OF_REPORTS.APP_PHOTO_SCORE:
      case TYPES_OF_REPORTS.EXTERNAL_PHOTO_SCORE:
        base = [
          ["name", "STUDYID", 300],
          ["site_id", "SITENUM", 150],
          ["participant_number", "SCREENNUM", 150],
          ["visit_name", "VISITNUM", 300],
          ["visit_status", "VISITSTATUS", 150],
          ["scored_by", "Scored By", 250],
          ["scored_at", "Scored At", 180],
          ["block_name", "FORMNAME", 300],
          ["key_value", "Value", 900],
        ];
        break;
      case TYPES_OF_REPORTS.ECONSENT:
        base = [
          ["trial_name", "STUDYID", 300],
          ["site_id", "SITENUM", 300],
          ["participant_number", "SCREENNUM", 300],
          ["first_name", "First Name", 200],
          ["last_name", "Last Name", 200],
          ["is_consent_signed", "Consent Signed", 180],
          ["consent_signed_date", "Consent Signed Date", 220],
        ];
        break;
      default:
        break;
    }

    return base;
  }, [trialDetails?.selected_report, role]);

  const columns = useMemo(() => {
    return (columnData || [])
      .map(([f, h, w, show]) => {
        if (!f || !h) return null;

        const col = {
          field: f,
          headerName: h,
          width: Number(w) || 150,
          // (Optional but harmless) provide defaults that GridColumnHeaderMenu may read:
          align: "left",
          headerAlign: "left",
        };

        if (CHARACTER_LIMITS[f]) {
          col.valueFormatter = (value) => truncateText(value, CHARACTER_LIMITS[f]);
        }

        if (f === "thumbnail") {
          col.renderCell = (params) => {
            const imageUrl = params.row.thumbnail?.length > 0 ? `${params.row.thumbnail}` : Avatar;
            return (
              <img
                src={imageUrl}
                alt="img"
                style={{
                  width: 40,
                  height: 46,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            );
          };
        }

        if (f === "video") {
          col.renderCell = (params) => {
            const videoUrl = params.row.video?.length > 0 ? `${params.row.video}` : null;
            return videoUrl ? (
              <div
                style={{
                  width: 40,
                  height: 46,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#e0e0e0",
                  cursor: "pointer",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  transition: "background-color 0.2s ease-in-out",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#d6d6d6")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#e0e0e0")}
              >
                <PlayCircleOutline style={{ fontSize: 32 }} />
              </div>
            ) : (
              <img
                src={Avatar}
                alt="img"
                style={{
                  width: 40,
                  height: 46,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            );
          };
        }

        if (f === "visit_number" && show) {
          col.renderCell = (params) => {
            const visitDisplay = params.row.visit_name ?? params.row.visit_number ?? "";
            return <div>{visitDisplay}</div>;
          };
        }

        return col;
      })
      .filter(Boolean);
  }, [columnData]);

  const defaultVisibilityModel = useMemo(() => {
    const model = {};
    (columnData || []).forEach(([field, , , hiddenByDefault]) => {
      if (!field) return;
      // true => visible, false => hidden
      model[field] = !hiddenByDefault;
    });
    return model;
  }, [columnData]);

  const safeGetRowId = (row) =>
    row.row ??
    row.id ??
    row.uid ??
    `${row.site_id || ""}-${row.participant_number || ""}-${
      row.image_name || row.video_name || row.created_at || ""
    }`;

  const dataGridRef = useRef(null);

  const shouldShowDataGrid =
    columnData.length > 0 &&
    trialDetails?.selected_report !== TYPES_OF_REPORTS.ECRF_CHANGE_LOGS &&
    trialDetails?.selected_report !== TYPES_OF_REPORTS.QUERY_CHANGE_LOGS;

  return (
    <Box
      sx={{
        height: "80vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0, py: 1 }}>
        {trialDetails?.selected_report === TYPES_OF_REPORTS.PHOTO && (
          <CustomButton
            onClick={downloadReport}
            disabled={!reportDownloadLink}
            isLoading={isZipLoading}
            sx={{ m: 2, ml: 0 }}
            size="small"
            data-testid="download-report-btn"
          >
            Download Images as ZIP
          </CustomButton>
        )}
        {trialDetails?.selected_report === TYPES_OF_REPORTS.EXTERNAL_PHOTOS && (
          <CustomButton
            isLoading={isZipLoading}
            onClick={downloadReport}
            disabled={!reportDownloadLink}
            sx={{ m: 2, ml: 0 }}
            size="small"
            data-testid="download-report-btn"
          >
            Download External Images as ZIP
          </CustomButton>
        )}
        {trialDetails?.selected_report === TYPES_OF_REPORTS.EXTERNAL_VIDEOS && (
          <CustomButton
            isLoading={isZipLoading}
            onClick={downloadReport}
            disabled={!reportDownloadLink}
            sx={{ m: 2, ml: 0 }}
            size="small"
            data-testid="download-report-btn"
          >
            Download External Videos as ZIP
          </CustomButton>
        )}
        {trialDetails?.selected_report === TYPES_OF_REPORTS.EXTERNAL_DOCUMENTS && (
          <CustomButton
            onClick={downloadReport}
            disabled={!reportDownloadLink}
            sx={{ m: 2, ml: 0 }}
            size="small"
            data-testid="download-report-btn"
          >
            Download External Documents as ZIP
          </CustomButton>
        )}
        {trialDetails?.selected_report === TYPES_OF_REPORTS.VIDEO && (
          <CustomButton
            isLoading={isZipLoading}
            onClick={downloadReport}
            disabled={!reportDownloadLink}
            sx={{ m: 2, ml: 0 }}
            size="small"
            data-testid="download-report-btn"
          >
            Download Videos as ZIP
          </CustomButton>
        )}
        {trialDetails?.selected_report === TYPES_OF_REPORTS.ECONSENT &&
          reportData?.[0]?.is_digital_consent && (
            <CustomButton
              isLoading={isZipLoading}
              onClick={downloadReport}
              disabled={!reportDownloadLink}
              sx={{ m: 2, ml: 0 }}
              size="small"
              data-testid="download-econsent-zip-btn"
            >
              Download Signed Consents as ZIP
            </CustomButton>
          )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {shouldShowDataGrid && (
          <DataGridPro
            key={trialDetails?.selected_report || "default"}
            ref={dataGridRef}
            sx={{ height: "100%" }}
            rows={reportData ?? []}
            columns={columns}
            getRowId={safeGetRowId}
            onRowClick={handleRowClick(trialDetails?.trials?.[0]?.id)}
            onColumnResize={() => setFilterConfigurationLastStateSaved(false)}
            filterMode="server"
            /* selection */
            checkboxSelection
            disableRowSelectionOnClick
            keepNonExistentRowsSelected
            showToolbar
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(newModel) => {
              setRowSelectionModel(newModel?.ids instanceof Set ? newModel : EMPTY_SELECTION);
            }}
            /* column visibility */
            initialState={{
              columns: { columnVisibilityModel: defaultVisibilityModel },
            }}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            /* pagination (server) */
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={totalCount ?? data?.count ?? 0}
            pageSizeOptions={[reportTableConfigurations.noOfRows]} // single page size
            /* sizing */
            getRowHeight={() => "auto"}
            /* toolbar & panels */
            localeText={{
              toolbarExport: "Export this View",
              ...(trialDetails?.selected_report === TYPES_OF_REPORTS.ECRF_DISCREPANCY && {
                noRowsLabel: "No discrepancies found for the selected filters.",
              }),
            }}
            slots={{ toolbar: CustomToolbar }}
            slotProps={{
              toolbar: {
                reportName,
                rows: reportData ?? [],
                selectedRows: rowSelectionModel,
                columns,
                columnVisibilityModel,
              },
              columnsPanel: {
                disableHideAllButton: false,
                disableShowAllButton: false,
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default ReportColumns;
